/**
 * Weather alert banner — shows active Environment Canada / US NWS alerts
 * for the vessel's position. Polls every 10 minutes via the backend.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ExclamationTriangleIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import api from '../../config/api';

const POLL_MS = 10 * 60 * 1000;

const Banner = styled.div`
  border-radius: 10px;
  padding: 0.65rem 1rem;
  margin-bottom: 0.75rem;
  background: ${(p) => (p.$rank >= 3 ? '#fee2e2' : p.$rank === 2 ? '#ffedd5' : '#fef9c3')};
  border: 1px solid ${(p) => (p.$rank >= 3 ? '#fca5a5' : p.$rank === 2 ? '#fdba74' : '#fde047')};
  color: #1f2937;
  font-size: 0.9rem;

  .head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-weight: 600;
  }

  .head svg { width: 20px; height: 20px; flex-shrink: 0; }
  .head .warn { color: ${(p) => (p.$rank >= 3 ? '#dc2626' : '#d97706')}; }
  .head .spacer { flex: 1; }
  .head button {
    background: none; border: none; cursor: pointer; padding: 2px; display: flex;
    color: #6b7280;
  }

  .detail {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    font-size: 0.85rem;
  }

  .source { color: #6b7280; font-size: 0.75rem; }
`;

export default function WeatherAlertBanner({ position }) {
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [dismissedKey, setDismissedKey] = useState(null);
  const positionRef = useRef(position);
  positionRef.current = position;

  // Effect keys off the ~1km grid cell, not the position object, so per-second
  // GPS updates don't tear the polling interval down and rebuild it.
  const cell = position
    ? `${position.latitude.toFixed(2)},${position.longitude.toFixed(2)}`
    : null;

  useEffect(() => {
    if (!cell) return undefined;

    const fetchAlerts = async () => {
      const pos = positionRef.current;
      if (!pos) return;
      try {
        const resp = await api.get('/api/weather/alerts', {
          params: { lat: pos.latitude, lon: pos.longitude },
        });
        setAlerts(resp.data?.alerts || []);
      } catch (e) {
        // Non-critical — leave previous alerts in place
      }
    };

    fetchAlerts();
    const timer = setInterval(fetchAlerts, POLL_MS);
    return () => clearInterval(timer);
  }, [cell]);

  if (alerts.length === 0) return null;

  const alertsKey = alerts.map((a) => a.title + a.expires).join('|');
  if (dismissedKey === alertsKey) return null;

  const worst = alerts[0];

  return (
    <Banner $rank={worst.severityRank} role="alert">
      <div className="head" onClick={() => setExpanded((v) => !v)}>
        <ExclamationTriangleIcon className="warn" />
        <span>
          {worst.title}
          {alerts.length > 1 ? ` (+${alerts.length - 1} more)` : ''}
          {worst.area ? ` — ${worst.area}` : ''}
        </span>
        <span className="spacer" />
        {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        <button
          title="Dismiss"
          onClick={(e) => {
            e.stopPropagation();
            setDismissedKey(alertsKey);
          }}
        >
          <XMarkIcon />
        </button>
      </div>
      {expanded && alerts.map((a, i) => (
        <div className="detail" key={i}>
          <strong>{a.title}</strong>
          {a.area ? ` — ${a.area}` : ''}
          {a.description && (
            <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
              {a.description.length > 600 ? `${a.description.slice(0, 600)}…` : a.description}
            </div>
          )}
          <div className="source">
            {a.source}
            {a.expires ? ` · until ${new Date(a.expires).toLocaleString()}` : ''}
          </div>
        </div>
      ))}
    </Banner>
  );
}
